import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams, Link } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, ArrowRight, Check } from "lucide-react";
import { COLLECTION_PAGES } from "../lib/collections";
import { Seo } from "../components/Seo";

const EASE = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
    <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, delay, ease: EASE }}
        className={className}
    >
        {children}
    </motion.div>
);

export default function CollectionPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const page = COLLECTION_PAGES[slug];
    const [active, setActive] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        setActive(null);
    }, [slug]);

    if (!page) {
        return (
            <div className="min-h-screen bg-[#0F0C08] flex flex-col items-center justify-center gap-4 px-5 text-center">
                <p className="font-display text-3xl text-[#F4EFEA]">Collection not found</p>
                <Link to="/" className="text-sm text-[#D4A373] hover:text-[#F4EFEA] transition-colors">
                    ← Back to all collections
                </Link>
            </div>
        );
    }

    const enquire = () => navigate("/", { state: { category: page.category } });
    const step = (dir) => setActive((a) => (a + dir + page.items.length) % page.items.length);

    return (
        <div data-testid={`collection-page-${slug}`} className="bg-[#0F0C08]">
            <Seo
                title={`Wholesale ${page.title} | FnB International`}
                description={page.description.slice(0, 155)}
                path={`/collections/${slug}`}
            />
            {/* Hero */}
            <section className="relative pt-40 pb-16 overflow-hidden grain">
                <div
                    className="absolute inset-0 pointer-events-none"
                    style={{ background: "radial-gradient(55% 45% at 75% 12%, rgba(197,139,73,0.14) 0%, rgba(197,139,73,0) 70%)" }}
                />
                <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE }}
                        className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#C58B49] mb-5"
                        data-testid="collection-series"
                    >
                        {page.series}
                    </motion.p>
                    <motion.h1
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
                        className="font-display font-semibold text-[#F4EFEA] text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02]"
                        data-testid="collection-title"
                    >
                        {page.title}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
                        className="mt-3 text-xs tracking-[0.2em] uppercase text-[#8C8075]"
                    >
                        {page.sub}
                    </motion.p>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
                        className="mt-6 max-w-2xl text-base sm:text-lg text-[#C2B7AD] leading-relaxed"
                        data-testid="collection-description"
                    >
                        {page.description}
                    </motion.p>
                </div>
            </section>

            {/* Details + CTA */}
            <section className="border-y border-[#2E2620] bg-[#14110F]">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 flex flex-col lg:flex-row lg:items-center gap-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3 flex-1" data-testid="collection-features">
                        {page.features.map((f) => (
                            <div key={f} className="flex items-center gap-3">
                                <Check size={14} strokeWidth={2} className="text-[#C58B49] shrink-0" />
                                <span className="text-sm text-[#C2B7AD]">{f}</span>
                            </div>
                        ))}
                    </div>
                    <button
                        data-testid="collection-enquire-btn"
                        onClick={enquire}
                        className="group shrink-0 inline-flex items-center gap-2 px-8 py-4 bg-[#C58B49] text-[#0F0C08] text-[13px] font-medium tracking-[0.16em] uppercase rounded-sm hover:bg-[#D4A373] transition-colors duration-300"
                    >
                        Enquire About This Collection
                        <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                </div>
            </section>

            {/* Product grid */}
            <section className="py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <Reveal className="mb-10">
                        <p className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#C58B49]">
                            From the Workshop — {page.items.length} pieces
                        </p>
                    </Reveal>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
                        {page.items.map((item, i) => (
                            <Reveal key={item.img} delay={(i % 3) * 0.08}>
                                <button
                                    data-testid={`collection-item-${i}`}
                                    onClick={() => setActive(i)}
                                    className="group relative w-full overflow-hidden rounded-md border border-[#2E2620] hover:border-[#C58B49]/50 transition-colors duration-500 text-left"
                                >
                                    <img
                                        src={item.img}
                                        alt={`${item.title} — FnB International`}
                                        loading="lazy"
                                        className="w-full aspect-square object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.05]"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F0C08]/85 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                    <div className="absolute inset-x-0 bottom-0 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                                        <span className="font-display text-lg font-semibold text-[#F4EFEA]">{item.title}</span>
                                    </div>
                                </button>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal className="mt-14 text-center">
                        <p className="text-sm text-[#8C8075]">
                            Every piece can be produced to your colors, hardware and branding.
                        </p>
                        <button
                            data-testid="collection-bottom-enquire-btn"
                            onClick={enquire}
                            className="mt-5 text-[13px] tracking-[0.16em] uppercase text-[#D4A373] hover:text-[#F4EFEA] transition-colors"
                        >
                            Start a wholesale enquiry →
                        </button>
                    </Reveal>
                </div>
            </section>

            {/* Lightbox */}
            <AnimatePresence>
                {active !== null && page.items[active] && (
                    <motion.div
                        data-testid="collection-lightbox"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[70] bg-[#0F0C08]/92 backdrop-blur-md flex items-center justify-center p-5"
                        onClick={() => setActive(null)}
                    >
                        <button
                            data-testid="collection-lightbox-close"
                            onClick={() => setActive(null)}
                            className="absolute top-6 right-6 w-11 h-11 rounded-full border border-[#4A3E34] text-[#E2D4C9] flex items-center justify-center hover:border-[#C58B49] hover:text-[#D4A373] transition-colors"
                            aria-label="Close"
                        >
                            <X size={18} />
                        </button>
                        <button
                            data-testid="collection-lightbox-prev"
                            onClick={(e) => { e.stopPropagation(); step(-1); }}
                            className="absolute left-4 sm:left-8 w-11 h-11 rounded-full border border-[#4A3E34] text-[#E2D4C9] flex items-center justify-center hover:border-[#C58B49] hover:text-[#D4A373] transition-colors"
                            aria-label="Previous"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <button
                            data-testid="collection-lightbox-next"
                            onClick={(e) => { e.stopPropagation(); step(1); }}
                            className="absolute right-4 sm:right-8 w-11 h-11 rounded-full border border-[#4A3E34] text-[#E2D4C9] flex items-center justify-center hover:border-[#C58B49] hover:text-[#D4A373] transition-colors"
                            aria-label="Next"
                        >
                            <ChevronRight size={18} />
                        </button>
                        <motion.figure
                            key={page.items[active].img}
                            initial={{ opacity: 0, scale: 0.96, y: 16 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ duration: 0.45, ease: EASE }}
                            className="max-w-3xl w-full"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={page.items[active].img}
                                alt={page.items[active].title}
                                className="w-full max-h-[74vh] object-contain rounded-md border border-[#2E2620]"
                                data-testid="collection-lightbox-image"
                            />
                            <figcaption className="mt-4 flex items-baseline justify-between gap-4">
                                <span className="font-display text-2xl font-semibold text-[#F4EFEA]">
                                    {page.items[active].title}
                                </span>
                                <span className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#C58B49] shrink-0">
                                    {page.series}
                                </span>
                            </figcaption>
                        </motion.figure>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
